import React, { useCallback, useEffect, useState } from "react";
import api, { errMsg } from "../api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Shield, ShieldCheck, ShieldOff, KeyRound, RefreshCw, Copy, Download, X } from "lucide-react";

/**
 * TwoFactorSetup — TOTP-based 2FA setup compatible with:
 *   - Microsoft Authenticator
 *   - Google Authenticator
 *   - Authy, 1Password, Bitwarden, etc.
 *
 * Three-step flow:
 *   1) Enter current password → server generates secret + QR code
 *   2) Scan QR in Authenticator app → enter 6-digit code
 *   3) Server returns one-time backup codes — store securely
 */
export default function TwoFactorSetup({ lang = "de" }) {
    const t = (de, en) => (lang === "de" ? de : en);
    const [status, setStatus] = useState({ enabled: false, backup_codes_remaining: 0 });
    const [step, setStep] = useState("idle"); // idle | password | scan | success | disable | regenerate
    const [password, setPassword] = useState("");
    const [code, setCode] = useState("");
    const [setupData, setSetupData] = useState(null); // { secret, qr_code, uri }
    const [backupCodes, setBackupCodes] = useState([]);
    const [busy, setBusy] = useState(false);

    const loadStatus = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/2fa/status");
            setStatus(data);
        } catch (e) { toast.error(errMsg(e)); }
    }, []);

    useEffect(() => { loadStatus(); }, [loadStatus]);

    const reset = () => {
        setStep("idle"); setPassword(""); setCode(""); setSetupData(null); setBackupCodes([]);
    };

    const startSetup = async () => {
        if (!password) return toast.error(t("Bitte Passwort eingeben", "Please enter password"));
        setBusy(true);
        try {
            const { data } = await api.post("/auth/2fa/setup", { password });
            setSetupData(data);
            setStep("scan");
        } catch (e) { toast.error(errMsg(e)); }
        finally { setBusy(false); }
    };

    const verifyAndEnable = async () => {
        if (!code || code.length < 6) return toast.error(t("6-stelligen Code eingeben", "Enter 6-digit code"));
        setBusy(true);
        try {
            const { data } = await api.post("/auth/2fa/verify", { code });
            setBackupCodes(data.backup_codes || []);
            setStep("success");
            toast.success(t("2FA aktiviert!", "2FA activated!"));
            await loadStatus();
        } catch (e) { toast.error(errMsg(e)); }
        finally { setBusy(false); }
    };

    const disable = async () => {
        if (!password || !code) return toast.error(t("Passwort + 2FA-Code erforderlich", "Password + 2FA code required"));
        setBusy(true);
        try {
            await api.post("/auth/2fa/disable", { password, code });
            toast.success(t("2FA deaktiviert", "2FA disabled"));
            reset();
            await loadStatus();
        } catch (e) { toast.error(errMsg(e)); }
        finally { setBusy(false); }
    };

    const regenerateBackup = async () => {
        if (!password || !code) return toast.error(t("Passwort + aktuellen 2FA-Code erforderlich", "Password + current 2FA code required"));
        setBusy(true);
        try {
            const { data } = await api.post("/auth/2fa/regenerate-backup-codes", { password, code });
            setBackupCodes(data.backup_codes || []);
            setStep("success");
            toast.success(t("Neue Backup-Codes erstellt", "New backup codes created"));
            await loadStatus();
        } catch (e) { toast.error(errMsg(e)); }
        finally { setBusy(false); }
    };

    const copyBackup = () => {
        const text = backupCodes.join("\n");
        navigator.clipboard.writeText(text);
        toast.success(t("Codes in Zwischenablage kopiert", "Codes copied to clipboard"));
    };

    const downloadBackup = () => {
        const blob = new Blob([`MyTACS 2FA Backup-Codes\nGeneriert: ${new Date().toLocaleString("de-DE")}\n\n${backupCodes.join("\n")}\n\nBewahre diese Codes sicher auf. Jeder Code kann nur EINMAL zum Login verwendet werden.\n`], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "mytacs-2fa-backup-codes.txt";
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="rounded-lg border border-neutral-200 bg-white p-5" data-testid="twofa-section">
            <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        status.enabled ? "bg-gradient-to-br from-emerald-500 to-teal-600" : "bg-neutral-100"
                    }`}>
                        {status.enabled ? <ShieldCheck className="w-5 h-5 text-white" /> : <Shield className="w-5 h-5 text-neutral-400" />}
                    </div>
                    <div>
                        <p className="text-sm font-bold">{t("Zwei-Faktor-Authentifizierung (2FA)", "Two-factor authentication (2FA)")}</p>
                        <p className="text-xs text-neutral-500 mt-0.5 max-w-xl">
                            {t("Schütze deinen Account zusätzlich mit einem 6-stelligen Code aus Microsoft Authenticator, Google Authenticator, Authy oder 1Password.",
                               "Protect your account with a 6-digit code from Microsoft Authenticator, Google Authenticator, Authy or 1Password.")}
                        </p>
                    </div>
                </div>
                {status.enabled && (
                    <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-1 rounded font-bold whitespace-nowrap" data-testid="twofa-status-active">
                        {t("AKTIV", "ACTIVE")} · {status.backup_codes_remaining} {t("Backup-Codes übrig", "backup codes left")}
                    </span>
                )}
            </div>

            {/* === Idle state — show enable or manage button === */}
            {step === "idle" && (
                <div className="flex flex-wrap gap-2 mt-3">
                    {!status.enabled ? (
                        <Button onClick={() => setStep("password")} className="bg-brand-500 hover:bg-brand-600 text-white gap-1.5" data-testid="twofa-enable-btn">
                            <Shield className="w-3.5 h-3.5" />{t("2FA aktivieren", "Enable 2FA")}
                        </Button>
                    ) : (
                        <>
                            <Button onClick={() => setStep("regenerate")} variant="outline" className="gap-1.5" data-testid="twofa-regenerate-btn">
                                <RefreshCw className="w-3.5 h-3.5" />{t("Backup-Codes neu erstellen", "Regenerate backup codes")}
                            </Button>
                            <Button onClick={() => setStep("disable")} variant="outline" className="gap-1.5 text-red-600 hover:bg-red-50" data-testid="twofa-disable-btn">
                                <ShieldOff className="w-3.5 h-3.5" />{t("2FA deaktivieren", "Disable 2FA")}
                            </Button>
                        </>
                    )}
                </div>
            )}

            {/* === Step 1: Password === */}
            {step === "password" && (
                <div className="mt-3 p-4 bg-neutral-50 rounded-lg space-y-3" data-testid="twofa-step-password">
                    <p className="text-xs font-semibold">{t("Schritt 1 von 3: Aktuelles Passwort bestätigen", "Step 1 of 3: Confirm current password")}</p>
                    <input
                        type="password"
                        autoFocus
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && startSetup()}
                        placeholder={t("Aktuelles Passwort", "Current password")}
                        className="w-full h-10 border border-neutral-200 rounded px-3 text-sm focus:border-blue-300 focus:ring-2 focus:ring-blue-100 outline-none"
                        data-testid="twofa-password-input"
                    />
                    <div className="flex gap-2 justify-end">
                        <Button variant="outline" onClick={reset}>{t("Abbrechen", "Cancel")}</Button>
                        <Button onClick={startSetup} disabled={busy} className="bg-brand-500 text-white" data-testid="twofa-start-setup">
                            {busy ? "…" : t("Weiter", "Continue")}
                        </Button>
                    </div>
                </div>
            )}

            {/* === Step 2: Scan QR === */}
            {step === "scan" && setupData && (
                <div className="mt-3 p-4 bg-neutral-50 rounded-lg space-y-3" data-testid="twofa-step-scan">
                    <p className="text-xs font-semibold">{t("Schritt 2 von 3: QR-Code in Authenticator-App scannen", "Step 2 of 3: Scan QR code in your Authenticator app")}</p>
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-shrink-0">
                            <img src={setupData.qr_code} alt="2FA QR Code" className="w-48 h-48 border-2 border-neutral-200 rounded-lg" data-testid="twofa-qr-image" />
                        </div>
                        <div className="flex-1 space-y-3 text-xs">
                            <p className="text-neutral-700 leading-relaxed">
                                {t("Öffne ", "Open ")}
                                <strong>Microsoft Authenticator</strong>, <strong>Google Authenticator</strong>{t(" oder ", " or ")}<strong>Authy</strong>,
                                {t(" tippe auf '+' und wähle 'QR-Code scannen'. Halte die Kamera auf den QR-Code links.",
                                   " tap on '+' and choose 'Scan QR code'. Point the camera at the QR code on the left.")}
                            </p>
                            <details className="bg-white border border-neutral-200 rounded p-2.5">
                                <summary className="cursor-pointer font-semibold text-neutral-600">{t("Code manuell eingeben (statt scannen)?", "Enter code manually instead?")}</summary>
                                <div className="mt-2 pt-2 border-t border-neutral-100 space-y-1">
                                    <p>{t("Konto-Name:", "Account name:")} <code className="bg-neutral-100 px-1.5 py-0.5 rounded">{setupData.label}</code></p>
                                    <p>{t("Schlüssel:", "Secret key:")} <code className="bg-neutral-100 px-1.5 py-0.5 rounded font-mono text-[10px] break-all">{setupData.secret}</code></p>
                                </div>
                            </details>
                            <div className="pt-2">
                                <label className="block font-semibold mb-1">
                                    {t("Schritt 3: Gib den 6-stelligen Code aus deiner App ein", "Step 3: Enter the 6-digit code from your app")}
                                </label>
                                <input
                                    autoFocus
                                    inputMode="numeric"
                                    pattern="\d{6}"
                                    maxLength={6}
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                                    onKeyDown={(e) => e.key === "Enter" && verifyAndEnable()}
                                    placeholder="123456"
                                    className="w-32 h-12 border-2 border-neutral-200 rounded-lg px-3 text-2xl text-center tracking-widest font-mono focus:border-blue-500 outline-none"
                                    data-testid="twofa-code-input"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-2 justify-end">
                        <Button variant="outline" onClick={reset}>{t("Abbrechen", "Cancel")}</Button>
                        <Button onClick={verifyAndEnable} disabled={busy || code.length !== 6} className="bg-brand-500 text-white" data-testid="twofa-verify-btn">
                            {busy ? "…" : t("Aktivieren", "Activate")}
                        </Button>
                    </div>
                </div>
            )}

            {/* === Step "success" — show backup codes === */}
            {step === "success" && backupCodes.length > 0 && (
                <div className="mt-3 p-4 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg space-y-3" data-testid="twofa-backup-codes">
                    <div className="flex items-start gap-2">
                        <KeyRound className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm font-bold text-amber-900">{t("Backup-Codes — JETZT speichern!", "Backup codes — save NOW!")}</p>
                            <p className="text-xs text-amber-800 mt-0.5">
                                {t("Diese 10 Codes ermöglichen dir den Login, wenn du dein Handy verlierst. Jeder Code kann nur EINMAL verwendet werden. Wir können sie nicht erneut anzeigen.",
                                   "These 10 codes let you log in if you lose your phone. Each can be used ONCE. We can't show them again.")}
                            </p>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-1.5 font-mono text-sm bg-white p-3 rounded border border-amber-200">
                        {backupCodes.map((c, i) => (
                            <code key={i} className="px-2 py-1 bg-neutral-50 rounded text-center" data-testid={`twofa-backup-${i}`}>{c}</code>
                        ))}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button onClick={copyBackup} variant="outline" size="sm" className="gap-1.5"><Copy className="w-3 h-3" />{t("Kopieren", "Copy")}</Button>
                        <Button onClick={downloadBackup} variant="outline" size="sm" className="gap-1.5"><Download className="w-3 h-3" />{t("Als .txt herunterladen", "Download as .txt")}</Button>
                        <Button onClick={reset} className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1.5 ml-auto"><X className="w-3 h-3" />{t("Codes habe ich gespeichert — schließen", "I saved them — close")}</Button>
                    </div>
                </div>
            )}

            {/* === Step "disable" === */}
            {step === "disable" && (
                <div className="mt-3 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg space-y-3" data-testid="twofa-step-disable">
                    <p className="text-sm font-bold text-red-900">{t("2FA deaktivieren — Sicherheit erheblich verringert", "Disable 2FA — significantly weakens security")}</p>
                    <p className="text-xs text-red-800">{t("Bestätige mit deinem Passwort und einem aktuellen 2FA-Code (oder Backup-Code).", "Confirm with your password and a current 2FA code (or backup code).")}</p>
                    <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("Aktuelles Passwort", "Current password")} className="w-full h-10 border border-neutral-200 rounded px-3 text-sm outline-none focus:border-red-300" data-testid="twofa-disable-password" />
                    <input value={code} onChange={(e) => setCode(e.target.value)} placeholder={t("Aktueller 2FA-Code oder Backup-Code", "Current 2FA code or backup code")} className="w-full h-10 border border-neutral-200 rounded px-3 text-sm font-mono outline-none focus:border-red-300" data-testid="twofa-disable-code" />
                    <div className="flex gap-2 justify-end">
                        <Button variant="outline" onClick={reset}>{t("Abbrechen", "Cancel")}</Button>
                        <Button onClick={disable} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="twofa-disable-confirm">
                            {busy ? "…" : t("2FA deaktivieren", "Disable 2FA")}
                        </Button>
                    </div>
                </div>
            )}

            {/* === Step "regenerate" backup codes === */}
            {step === "regenerate" && (
                <div className="mt-3 p-4 bg-neutral-50 rounded-lg space-y-3" data-testid="twofa-step-regenerate">
                    <p className="text-sm font-semibold">{t("Backup-Codes neu erstellen", "Regenerate backup codes")}</p>
                    <p className="text-xs text-neutral-600">{t("Die alten Codes werden ungültig. Bestätige mit Passwort und aktuellem 2FA-Code.", "Old codes will be invalidated. Confirm with password and current 2FA code.")}</p>
                    <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("Aktuelles Passwort", "Current password")} className="w-full h-10 border border-neutral-200 rounded px-3 text-sm outline-none" data-testid="twofa-regen-password" />
                    <input value={code} onChange={(e) => setCode(e.target.value)} placeholder={t("Aktueller 2FA-Code", "Current 2FA code")} className="w-full h-10 border border-neutral-200 rounded px-3 text-sm font-mono outline-none" data-testid="twofa-regen-code" />
                    <div className="flex gap-2 justify-end">
                        <Button variant="outline" onClick={reset}>{t("Abbrechen", "Cancel")}</Button>
                        <Button onClick={regenerateBackup} disabled={busy} className="bg-brand-500 text-white" data-testid="twofa-regen-confirm">
                            {busy ? "…" : t("Neu erstellen", "Regenerate")}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
